/**
 * Cache error handling utilities
 */

export enum CacheErrorType {
  STORAGE_FULL = 'STORAGE_FULL',
  SERIALIZATION_ERROR = 'SERIALIZATION_ERROR',
  RETRIEVAL_ERROR = 'RETRIEVAL_ERROR',
  CLEANUP_ERROR = 'CLEANUP_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class CacheError extends Error {
  type: CacheErrorType;
  key?: string;
  originalError?: Error;
  
  constructor(type: CacheErrorType, message: string, key?: string, originalError?: Error) {
    super(message);
    this.name = 'CacheError';
    this.type = type;
    this.key = key;
    this.originalError = originalError;
  }
}

/**
 * Centralized cache error logger
 */
export class CacheErrorLogger {
  private static errors: CacheError[] = [];
  private static maxErrors = 100;
  
  static log(error: CacheError): void {
    this.errors.push(error);
    
    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }
    
    // Log to console with structured format
    console.error('[Cache Error]', {
      type: error.type,
      message: error.message,
      key: error.key,
      timestamp: new Date().toISOString(),
      stack: error.stack
    });
    
    // Could also send to monitoring service here
    this.sendToMonitoring(error);
  }
  
  static getRecentErrors(count: number = 10): CacheError[] {
    return this.errors.slice(-count);
  }
  
  static getErrorStats(): Record<CacheErrorType, number> {
    const stats: Record<string, number> = {};
    
    for (const errorType of Object.values(CacheErrorType)) {
      stats[errorType] = 0;
    }
    
    this.errors.forEach(error => {
      stats[error.type]++;
    });
    
    return stats as Record<CacheErrorType, number>;
  }
  
  static clearErrors(): void {
    this.errors = [];
  }
  
  private static sendToMonitoring(error: CacheError): void {
    // Placeholder for sending errors to monitoring service
    // In production, this would send to services like Sentry, DataDog, etc.
    if (process.env.NODE_ENV === 'production') {
      // Example: window.Sentry?.captureException(error);
    }
  }
}

/**
 * Wrapper for safe cache operations with error handling
 */
export function withCacheErrorHandling<T extends (...args: any[]) => any>(
  fn: T,
  errorType: CacheErrorType,
  getKey?: (...args: Parameters<T>) => string
): T {
  return ((...args: Parameters<T>) => {
    try {
      const result = fn(...args);
      
      // Handle async functions
      if (result instanceof Promise) {
        return result.catch((error: Error) => {
          const key = getKey ? getKey(...args) : undefined;
          const cacheError = new CacheError(
            errorType,
            `Cache operation failed: ${error.message}`,
            key,
            error
          );
          CacheErrorLogger.log(cacheError);
          throw cacheError;
        });
      }
      
      return result;
    } catch (error: any) {
      const key = getKey ? getKey(...args) : undefined;
      const cacheError = new CacheError(
        errorType,
        `Cache operation failed: ${error.message}`,
        key,
        error
      );
      CacheErrorLogger.log(cacheError);
      throw cacheError;
    }
  }) as T;
}

/**
 * Retry wrapper for cache operations
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 100
): Promise<T> {
  let lastError: Error | undefined;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      
      if (i < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, i);
        console.warn(`[Cache] Operation failed, retrying in ${delay}ms...`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError || new Error('Cache operation failed after retries');
}

/**
 * Cache operation result type
 */
export type CacheResult<T> = {
  success: true;
  data: T;
} | {
  success: false;
  error: CacheError;
};

/**
 * Safe cache operation wrapper that returns Result type
 */
export async function safeCacheOperation<T>(
  operation: () => Promise<T>,
  errorType: CacheErrorType,
  key?: string
): Promise<CacheResult<T>> {
  try {
    const data = await operation();
    return { success: true, data };
  } catch (error: any) {
    const cacheError = error instanceof CacheError 
      ? error 
      : new CacheError(errorType, error.message, key, error);
    
    CacheErrorLogger.log(cacheError);
    return { success: false, error: cacheError };
  }
}